import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { CSSProperties, MouseEvent, ReactNode } from "react";

export { AnimatePresence };

const animations = {
  attention: {
    animate: { scale: [1, 1.16, 0.96, 1.08, 1], rotate: [0, -8, 6, -3, 0] },
    transition: { duration: 1.4, repeat: Infinity, ease: "easeInOut" as const },
  },
  pop: {
    initial: { scale: 0.6 },
    animate: { scale: 1 },
    transition: { duration: 0.2, ease: "easeOut" as const },
  },
  rise: {
    initial: { y: "110%", scale: 0.96 },
    animate: { y: 0, scale: 1 },
    exit: {
      y: "110%",
      transition: {
        type: "tween" as const,
        duration: 0.18,
        ease: "easeIn" as const,
      },
    },
    transition: {
      type: "spring" as const,
      stiffness: 380,
      damping: 16,
      mass: 0.7,
    },
  },
  nudge: {
    initial: { scale: 1 },
    animate: { scale: [1, 1.06, 0.98, 1.02, 1] },
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
  scale: {
    initial: { scale: 0 },
    animate: {
      scale: 1,
      transition: { duration: 0.18, ease: "easeOut" as const },
    },
    exit: {
      scale: 0,
      transition: { duration: 0.22, ease: "easeIn" as const },
    },
  },
};

type MotionName = keyof typeof animations;

const tags = {
  span: motion.span,
  div: motion.div,
  button: motion.button,
};

type MotionTag = keyof typeof tags;

const Motion = ({
  as = "span",
  name,
  className,
  children,
  ...props
}: {
  as?: MotionTag;
  name?: MotionName;
  className?: string;
  children?: ReactNode;
  id?: string;
  style?: CSSProperties;
  type?: "button" | "submit" | "reset";
  onClick?: (event: MouseEvent) => void;
  "aria-label"?: string;
  whileTap?: any;
}) => {
  const reduceMotion = useReducedMotion();
  const preset = name ? animations[name] : undefined;
  const looping =
    !!preset &&
    "transition" in preset &&
    "repeat" in preset.transition &&
    preset.transition.repeat === Infinity;
  const Tag = tags[as];
  const active =
    as === "button"
      ? "transition-transform duration-150 ease-out active:scale-95"
      : "";

  return (
    <Tag
      className={[active, className].filter(Boolean).join(" ")}
      {...(reduceMotion && looping ? {} : preset)}
      {...props}
    >
      {children}
    </Tag>
  );
};

export default Motion;
